import { useCallback } from 'react'
import {
  BenefitLinkClickedEventData,
  ErrorCapturedEventData,
  MMPPolicyActionEventData,
  NavigationMenuEventData,
  PayClickedEventData,
  PaymentFailedEventData,
  PaymentSuccessEventData,
  PaymentStartedEventData,
  PolicyProfessionInfoEventData,
  PolicyRenewalFailedEventData,
  PolicyRenewalStartedEventData,
  PolicyRenewedEventData,
  PurchasedPolicyOptionEventData,
  QuotationInfoEventData,
  QuoteViewedEventData,
  GTMEvent,
  GTM_FLOW_NAME,
} from '../types'
import { QuotationModel } from '@/features/quote-list/types'
import { parseDiscounts } from '@/features/quote-list/utils/discount-parser'
import { getEnv } from '@/lib/config/runtime-env'

const isGTMEnabled = (): boolean => {
  const { GTM_ID, GTM_ENABLED } = getEnv()
  return !!globalThis.window && !!GTM_ID && GTM_ENABLED
}

const sendEvent = (event: GTMEvent): void => {
  if (!isGTMEnabled()) {
    console.warn('[GTM] not enabled — event not sent')
    return
  }

  try {
    ;(window as any).dataLayer = (window as any).dataLayer ?? []
    ;(window as any).dataLayer.push(event)
  } catch (error) {
    console.error('Failed to send GTM event:', error)
  }
}

export const useGTMTracking = () => {
  // Purchase Flow Tracking - MMP specific
  const trackPurchaseStart = useCallback(
    (data?: { dobMonth?: string; dobYear?: string; userId?: string }) => {
      sendEvent({
        event: 'purchase_start',
        flow_name: GTM_FLOW_NAME,
        dob_month: data?.dobMonth,
        dob_year: data?.dobYear,
        user_id: data?.userId,
      })
    },
    []
  )

  // MM Policyholder Details Tracking — PRD: Saudi ID/IQAMA, DOB Month, DOB Year
  const trackPolicyholderDetails = useCallback(
    (data: {
      identityNumber: string
      monthOfBirth: string
      yearOfBirth: number
    }) => {
      sendEvent({
        event: 'mm_policyholder_details',
        flow_name: GTM_FLOW_NAME,
        identity_number: data.identityNumber,
        dob_month: data.monthOfBirth,
        dob_year: data.yearOfBirth,
      })
    },
    []
  )

  // Profession Data Tracking
  const trackAddProfessionData = useCallback(
    (professionInfo: PolicyProfessionInfoEventData) => {
      sendEvent({
        event: 'add_profession_data',
        step_name: 'add profession data',
        experience: professionInfo.yearsOfExperience,
        region: professionInfo.regionOfPractice,
        place: professionInfo.placeOfPractice,
        speciality: professionInfo.specialty,
        surgeon: professionInfo.isPerformingSurgeries ? 'yes' : 'no',
        policyEffectiveDate: professionInfo.policyEffectiveDate,
      })
    },
    []
  )

  // Declarations Data Tracking
  const trackAddDeclarationsDetailsData = useCallback(
    (trackingValues: Record<string, boolean>) => {
      sendEvent({
        event: 'add_declarations_details_data',
        flow_name: 'mmp',
        step_name: 'add declarations details data',
        ...Object.fromEntries(
          Object.entries(trackingValues).map(([key, value]) => [
            key,
            value ? 'yes' : 'no',
          ])
        ),
      })
    },
    []
  )

  // Terms and Conditions Tracking
  const trackTermsAndConditions = useCallback((companyName: string) => {
    sendEvent({
      event: 'terms_and_conditions',
      flow_name: 'mmp',
      step_name: 'quote list',
      quote: companyName,
    })
  }, [])

  // Update Quota Detail Tracking
  const trackUpdateQuotaDetail = useCallback(
    (companyName: string, selectedField: string, selectedValue: string) => {
      sendEvent({
        event: 'update_quota_detail',
        flow_name: 'mmp',
        step_name: 'quote list',
        quote: companyName,
        selected_field: selectedField,
        selected_value: selectedValue,
      })
    },
    []
  )

  const trackQuotationsReceived = useCallback(
    (quotationsReceived: QuotationModel[], specialty?: string) => {
      if (!quotationsReceived.length) return

      let lowestPrice = Infinity
      let highestPrice = -Infinity
      let lowestDiscountedPrice = Infinity
      let highestDiscountedPrice = -Infinity
      const allInsurerNames: string[] = []

      const quotations = quotationsReceived.map((q) => {
        const firstDeductible = q.durations[0].coverPlans[0].deductibles[0]
        const price = firstDeductible.taxableAmountWithVat
        const discounts = parseDiscounts(firstDeductible.discounts || '')
        const totalDiscountAmount = discounts.reduce(
          (sum, d) => sum + (d.Amount ?? 0),
          0
        )
        const discountedPrice = price - totalDiscountAmount
        const hasDiscount = discounts.length > 0

        if (price < lowestPrice) lowestPrice = price
        if (price > highestPrice) highestPrice = price
        if (discountedPrice < lowestDiscountedPrice)
          lowestDiscountedPrice = discountedPrice
        if (discountedPrice > highestDiscountedPrice)
          highestDiscountedPrice = discountedPrice

        return {
          insurer_name: q.insuranceCompany.nameEN,
          ic_product: q.icProduct?.descriptionEn,
          policy_id: q.applicationReference,
          price,
          discount_price: discountedPrice,
          available_plan_names: q.durations[0].coverPlans
            .map((p) => p.reference)
            .join(','),
          split_option_available: false,
          discount_applied: hasDiscount,
          discount_name: discounts[0]?.NameEn ?? '',
          discount_amount: discounts[0]?.Amount ?? 0,
          total_discount_amount: totalDiscountAmount,
          policy_duration: q.durations[0].value,
          policy_limit: q.durations[0].coverPlans[0].oneTimeOccurrence,
          deductible: firstDeductible.deductibleID,
          retroactive_cover: q.retroactivePeriod?.isCompulsory
            ? String(q.retroactivePeriod.compulsoryPeriod?.period ?? '')
            : '',
          erp: String(q.extendedReportingPeriod?.periods?.[0]?.value ?? ''),
        }
      })

      // Group plans by company: "Company (Plan1), (Plan2)"
      const groupedByCompany = new Map<string, string[]>()
      for (const q of quotations) {
        const plans = groupedByCompany.get(q.insurer_name) ?? []
        if (q.ic_product) plans.push(q.ic_product)
        groupedByCompany.set(q.insurer_name, plans)
      }
      for (const [company, plans] of Array.from(groupedByCompany.entries())) {
        if (plans.length > 0) {
          allInsurerNames.push(`${company} (${plans.join('), (')})`)
        } else {
          allInsurerNames.push(company)
        }
      }

      sendEvent({
        event: 'quotes_received',
        flow_name: GTM_FLOW_NAME,
        step_name: 'quote list',
        quotation_id: quotationsReceived[0].quotationReference,
        no_of_quotes: quotationsReceived.length,
        specialty,
        lowest_price: lowestPrice,
        lowest_discounted_price:
          lowestDiscountedPrice === Infinity ? 0 : lowestDiscountedPrice,
        highest_price: highestPrice,
        highest_discounted_price:
          highestDiscountedPrice === -Infinity ? 0 : highestDiscountedPrice,
        all_insurer_names: allInsurerNames,
        quotations,
      })
    },
    []
  )

  const trackQuoteViewed = useCallback((quoteData: QuoteViewedEventData) => {
    sendEvent({
      event: 'mm_quote_viewed',
      flow_name: GTM_FLOW_NAME,
      insurer_name: quoteData.insurerName,
      quotation_id: quoteData.quotationId,
      policy_number: quoteData.policyNumber,
      policy_duration: quoteData.policyDuration,
      policy_limit: quoteData.policyLimit,
      deductible: quoteData.deductible,
      price: quoteData.price,
      retroactive_cover: quoteData.retroactiveCover,
      erp: quoteData.ERP,
      no_of_benefit: quoteData.noOfBenefit,
      discount_yearly: quoteData.discountYearly,
      benefits: quoteData.benefits,
    })
  }, [])

  const trackBenefitLinkClicked = useCallback(
    (data: BenefitLinkClickedEventData) => {
      sendEvent({
        event: 'mm_benefit_link_clicked',
        flow_name: GTM_FLOW_NAME,
        benefit_title: data.benefitTitle,
        insurer_name: data.insurerName,
        quotation_id: data.quotationId,
        policy_number: data.policyNumber,
        benefit_link: data.benefitLink,
      })
    },
    []
  )

  // Select Item Tracking (Ecommerce)
  const trackSelectItem = useCallback((quoteData: QuotationInfoEventData) => {
    sendEvent({
      event: 'select_item',
      ecommerce: {
        item_list_name: 'mmp',
        item_list_id: quoteData.quotationId,
        items: {
          item_name: quoteData.insurerName,
          item_id: quoteData.quotationId,
          item_brand: quoteData.insurerName,
          item_category: quoteData.icProductId.toString(),
          currency: 'SAR',
          discount: quoteData.discount.toString(),
          index: quoteData.index.toString(),
          item_list_id: quoteData.policyNumber,
          item_list_name: 'mmp',
          price: quoteData.price.toString(),
          flow_name: 'mmp',
          retroactive_cover: quoteData.retroactiveCover,
          deductible: String(quoteData.deductible),
          policy_duration: quoteData.policyDuration.toString(),
          policy_limit: quoteData.policyLimit.toString(),
          erp: quoteData.ERP,
        },
      },
    })
  }, [])

  const trackPayClicked = useCallback((data: PayClickedEventData) => {
    sendEvent({
      event: 'mm_pay_clicked',
      flow_name: GTM_FLOW_NAME,
      qualification: data.qualification,
      place_of_work: data.placeOfWork,
      quotation_id: data.quotationId,
      insurer_name: data.insurerName,
      policy_number: data.policyNumber,
      policy_duration: data.policyDuration,
      policy_limit: data.policyLimit,
      deductible: data.deductible,
      policy_price_amount: data.policyPriceAmount,
      extended_reporting_period: data.extendedReportingPeriod,
      retroactive_cover: data.retroactiveCover,
      subtotal: data.subtotal,
      vat: data.vat,
      total_amount: data.totalAmount,
      schs_number: data.schsNumber,
      schs_expiry_date: data.schsExpiryDate,
      place_of_practice: data.placeOfPractice,
    })
  }, [])

  // Begin Checkout Tracking (Ecommerce) — also carries PRD flat attributes
  const trackPolicyPaymentStarted = useCallback(
    (checkoutData: PaymentStartedEventData) => {
      sendEvent({
        event: 'begin_checkout',
        flow_name: GTM_FLOW_NAME,
        coupon_code_applied: checkoutData.couponCodeApplied ?? false,
        coupon_code: checkoutData.couponCode ?? '',
        coupon_code_amount: checkoutData.couponCodeAmount ?? 0,
        quotation_id: checkoutData.quotationId,
        insurance_company: checkoutData.insuranceCompany,
        policy_number: checkoutData.policyNumber,
        invoice_value: checkoutData.invoiceValue,
        vat_percentage: Number(checkoutData.vatPercentage) || 0,
        vat_amount: Number(checkoutData.vatAmount) || 0,
        total_amount: Number(checkoutData.totalAmount) || 0,
        payment_method: checkoutData.paymentMethod ?? '',
        ecommerce: {
          currency: 'SAR',
          value: checkoutData.totalAmount,
          items: {
            item_name: checkoutData.insuranceCompany,
            item_id: checkoutData.policyNumber,
            item_brand: checkoutData.insuranceCompany,
            item_category: checkoutData.insuranceCompany,
            currency: 'SAR',
            discount: checkoutData.discount,
            index: '0',
            item_list_id: checkoutData.policyNumber,
            item_list_name: 'mmp',
            price: checkoutData.totalAmount,
            flow_name: 'mmp',
          },
        },
      })
    },
    []
  )

  // Purchase Complete Tracking (Ecommerce) — carries PRD flat attributes
  const trackPurchaseComplete = useCallback(
    (purchaseData: PaymentSuccessEventData) => {
      sendEvent({
        event: 'purchase',
        flow_name: GTM_FLOW_NAME,
        quotation_id: purchaseData.quotationId,
        insurer_name: purchaseData.insuranceCompany,
        policy_number: purchaseData.policyNumber,
        policy_duration: String(purchaseData.policyDuration ?? ''),
        policy_price_amount:
          Number(purchaseData.price ?? purchaseData.totalAmount) || 0,
        subtotal: Number(purchaseData.invoiceValue) || 0,
        vat: Number(purchaseData.vatAmount) || 0,
        total_amount: Number(purchaseData.totalAmount) || 0,
        payment_method: purchaseData.paymentMethod ?? '',
        ecommerce: {
          currency: 'SAR',
          value: purchaseData.totalAmount?.toString() ?? '',
          coupon: purchaseData.discount,
          transaction_id: purchaseData.policyNumber,
          tax: purchaseData.vatAmount?.toString() ?? '',
          items: {
            item_name: purchaseData.insuranceCompany,
            item_id: purchaseData.policyNumber,
            item_brand: purchaseData.insuranceCompany,
            item_category: purchaseData.insuranceCompanyCode?.toString() ?? '',
            currency: 'SAR',
            discount: purchaseData.discount,
            index: '0',
            item_list_id: purchaseData.policyNumber,
            item_list_name: 'mmp',
            price: purchaseData.totalAmount?.toString() ?? '',
            flow_name: 'mmp',
          },
        },
      })
    },
    []
  )

  const trackPaymentFailed = useCallback((data: PaymentFailedEventData) => {
    sendEvent({
      event: 'mm_policy_payment_failed',
      flow_name: GTM_FLOW_NAME,
      quotation_id: data.quotationId,
      insurer_name: data.insurerName,
      policy_number: data.policyNumber,
      policy_duration: data.policyDuration,
      policy_price_amount: data.policyPriceAmount,
      subtotal: data.subtotal,
      vat: data.vat,
      total_amount: data.totalAmount,
      payment_method: data.paymentMethod,
      status: data.status,
      payment_link: data.paymentLink,
    })
  }, [])

  // Summary Category Info Tracking
  const trackSummaryCategoryInfo = useCallback((category: string) => {
    sendEvent({
      event: 'summary_category_info',
      flow_name: 'mmp',
      step_name: 'summary & payment',
      category: category,
    })
  }, [])

  const trackNavigationMenu = useCallback((data: NavigationMenuEventData) => {
    sendEvent({
      event: 'navigation_menu',
      flow_name: GTM_FLOW_NAME,
      product: data.product,
      selection: data.selection,
    })
  }, [])

  const trackPurchasedPolicyOption = useCallback(
    (data: PurchasedPolicyOptionEventData) => {
      sendEvent({
        event: 'purchased_policy_option',
        flow_name: GTM_FLOW_NAME,
        selection: data.selection,
      })
    },
    []
  )

  const trackMMPPolicyAction = useCallback((data: MMPPolicyActionEventData) => {
    sendEvent({
      event: 'mmp_policy_action',
      flow_name: GTM_FLOW_NAME,
      policy_number: data.policyNumber,
      insurer_name: data.insurerName,
      years_of_experience: data.yearsOfExperience ?? 0,
      region_of_practice: data.regionOfPractice ?? '',
      place_of_practice: data.placeOfPractice ?? '',
      specialty: data.specialty ?? '',
      action: data.action,
    })
  }, [])

  const trackPolicyRenewalStarted = useCallback(
    (data: PolicyRenewalStartedEventData) => {
      sendEvent({
        event: 'policy_renewal_started',
        flow_name: GTM_FLOW_NAME,
        policy_number: data.policyNumber,
        previous_insurer: data.previousInsurer,
        coupon_code_applied: data.couponCodeApplied,
        coupon_code: data.couponCode,
        coupon_code_amount: data.couponCodeAmount,
        quotation_id: data.quotationId,
        insurance_company: data.insuranceCompany,
        invoice_value: data.invoiceValue,
        vat_percentage: data.vatPercentage,
        vat_amount: data.vatAmount,
        total_amount: data.totalAmount,
        payment_method: data.paymentMethod,
      })
    },
    []
  )

  const trackPolicyRenewed = useCallback((data: PolicyRenewedEventData) => {
    sendEvent({
      event: 'mm_policy_renewed',
      flow_name: GTM_FLOW_NAME,
      qualification: data.qualification,
      place_of_work: data.placeOfWork,
      quotation_id: data.quotationId,
      insurer_name: data.insurerName,
      policy_number: data.policyNumber,
      policy_duration: data.policyDuration,
      policy_limit: data.policyLimit,
      deductible: data.deductible,
      policy_price_amount: data.policyPriceAmount,
      extended_reporting_period: data.extendedReportingPeriod,
      retroactive_cover: data.retroactiveCover,
      subtotal: data.subtotal,
      vat: data.vat,
      total_amount: data.totalAmount,
      schs_number: data.schsNumber,
      schs_expiry_date: data.schsExpiryDate,
      place_of_practice: data.placeOfPractice,
      payment_method: data.paymentMethod,
    })
  }, [])

  const trackPolicyRenewalFailed = useCallback(
    (data: PolicyRenewalFailedEventData) => {
      sendEvent({
        event: 'mm_policy_renewal_failed',
        flow_name: GTM_FLOW_NAME,
        quotation_id: data.quotationId,
        insurer_name: data.insurerName,
        policy_number: data.policyNumber,
        policy_duration: data.policyDuration,
        policy_price_amount: data.policyPriceAmount,
        subtotal: data.subtotal,
        vat: data.vat,
        total_amount: data.totalAmount,
        payment_method: data.paymentMethod,
        status: data.status,
        payment_link: data.paymentLink,
      })
    },
    []
  )

  const trackErrorCaptured = useCallback((error: ErrorCapturedEventData) => {
    sendEvent({
      event: 'error_captured',
      flow_name: GTM_FLOW_NAME,
      step_name: error.stepName,
      error_code: error.errorCode,
      error_message: error.errorMessage,
      type: error.type,
    })
  }, [])

  const trackLogin = useCallback((data?: { userId?: string }) => {
    sendEvent({
      event: 'login',
      flow_name: GTM_FLOW_NAME,
      user_id: data?.userId,
    })
  }, [])

  const trackLogout = useCallback(() => {
    sendEvent({
      event: 'logout',
      flow_name: GTM_FLOW_NAME,
    })
  }, [])

  return {
    // Purchase flow tracking
    trackPurchaseStart,
    // Policyholder details tracking
    trackPolicyholderDetails,
    // Profession data tracking
    trackAddProfessionData,
    // Declarations data tracking
    trackAddDeclarationsDetailsData,
    // Terms and conditions tracking
    trackTermsAndConditions,
    // Update quota detail tracking
    trackUpdateQuotaDetail,
    // Select item tracking
    trackSelectItem,
    // Policy payment started tracking
    trackPolicyPaymentStarted,
    // Summary category info tracking
    trackSummaryCategoryInfo,
    // Purchase complete tracking
    trackPurchaseComplete,
    // Error tracking
    trackErrorCaptured,
    // Logout tracking
    trackLogout,
    // Login tracking
    trackLogin,
    trackQuotationsReceived,
    trackQuoteViewed,
    trackBenefitLinkClicked,
    trackPayClicked,
    trackPaymentFailed,
    trackNavigationMenu,
    trackPurchasedPolicyOption,
    trackMMPPolicyAction,
    trackPolicyRenewalStarted,
    trackPolicyRenewed,
    trackPolicyRenewalFailed,
  }
}
